import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, DoorOpen, Building2 } from "lucide-react";
import { statisticsApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import PageHeader from "@/components/PageHeader";
import { LoadingState, ErrorState, EmptyState } from "@/components/DataStates";
import { VerifyRepairDialog } from "@/components/VerifyRepairDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export default function Reinspections() {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useAsync(() => statisticsApi.pendingReinspections(), []);
  const [verifyDoor, setVerifyDoor] = useState(null);
  const doors = Array.isArray(data) ? data : [];

  return (
    <div className="space-y-6">
      <PageHeader title="Doors awaiting re-inspection" subtitle="Remedial works complete — verify these fire doors" testId="reinspections-header" />

      {loading && <LoadingState label="Loading doors…" rows={4} />}
      {error && <ErrorState onRetry={reload} />}
      {!loading && !error && (
        doors.length === 0 ? (
          <EmptyState title="Nothing awaiting re-inspection" message="When a maintenance job flagged for re-inspection is completed, the door shows up here." />
        ) : (
          <div className="space-y-2">
            {doors.map((d) => (
              <Card key={d.id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`reinspection-row-${d.id}`}>
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 font-semibold text-slate-800">
                    <DoorOpen className="h-4 w-4 text-primary" /> {d.door_reference || "Door"}
                  </p>
                  <p className="mt-0.5 flex items-center gap-1.5 text-sm text-slate-500">
                    <Building2 className="h-3.5 w-3.5" /> {d.building_name || "—"}{d.location ? ` · ${d.location}` : ""}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button variant="outline" size="sm" onClick={() => setVerifyDoor(d)} data-testid={`reinspection-verify-${d.id}`}>
                    <ShieldCheck className="mr-1.5 h-4 w-4" /> Verify repair
                  </Button>
                  <Button size="sm" onClick={() => navigate(`/inspections/new?doorId=${d.id}`)} data-testid={`reinspection-inspect-${d.id}`}>
                    Re-inspect
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )
      )}

      <VerifyRepairDialog
        doorId={verifyDoor?.id}
        doorRef={verifyDoor?.door_reference}
        open={!!verifyDoor}
        onOpenChange={(o) => !o && setVerifyDoor(null)}
        onDone={() => { setVerifyDoor(null); reload(); }}
      />
    </div>
  );
}
